// frontend/src/components/write/MetaData.jsx

import React, { useEffect, useRef, useState, useMemo } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import axios from "axios";
import { toast } from "react-toastify";
import { decodeHtml } from "../../utils/utils";
import "./metaData.css";
import {
  STAGE_ORDER,
  toHuman,
  stageColor,
} from "../../utils/stages";

export default function MetaData({
  mainAuthor, setMainAuthor,
  selectedAuthors, setSelectedAuthors,
  authors,
  editor, setEditor,
  editors,
  estWordCount, setEstWordCount,
  title, setTitle,
  seoTitle, setSeoTitle,
  metaDescription, setMetaDescription,
  focusKeyphrase, setFocusKeyphrase,
  postSlug, setPostSlug,
  selectedCategories, setSelectedCategories,
  categories,
  tags,
  selectedTags,
  handleTagChange,
  featuredImageData,
  setFeaturedImageData,
  setShowFeatureImageModal,
  toggleTooltip,
  visibleTooltip,
  finalDraftDue,
  stage,
  setStage,
  user,
  token,
  currentArticleId,
  locked,
  canEditMeta,
}) {

  const canAssignPeople = Number(user?.role) >= 4;    // only Lead Editor (role: 4) and Admin (role: 5)
  const isCreating = !currentArticleId;
  const canChangeStage = canAssignPeople && !!currentArticleId && canEditMeta;
  const [isSavingStage, setIsSavingStage] = useState(false);

  const warnedRef = useRef(false);

  // Auto-assign mainAuthor for writers/editors on create
  useEffect(() => {

    // Only when creating a new draft and the user is below role 4
    if (!isCreating || canAssignPeople) return;
    if (mainAuthor?.value) return;

    // Candidate IDs for this user (stringified)
    const candidateIds = [
      user?.authorId,  // legacy / future field
      user?.userId,    // numeric userId in your DB
      user?.id,        // whatever you store in Context
      user?._id,       // Mongo _id, in case you ever use it
    ]
    .filter(Boolean)
    .map(String);

    // If we don't yet have authors or IDs, just wait – **no toast yet**
    if (!authors || authors.length === 0 || candidateIds.length === 0) {
      return;
    }

    // Try to find this user in the authors list
    const myAuthorOpt = authors.find(a =>
      candidateIds.includes(String(a.value))
    );

    if (myAuthorOpt) {
      setMainAuthor(myAuthorOpt);
    } else if (!warnedRef.current) {
      toast.info("Your author profile isn't linked yet. It will be assigned automatically once synced.");
      warnedRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreating, canAssignPeople, authors, mainAuthor?.value, user?.authorId, user?.userId, user?.id, user?._id]);

  // Guard setters to block changes from roles < 4
  const safeSetMainAuthor = (opt) => {
    if (!canAssignPeople || !canEditMeta) return;    // ignore attempts
    setMainAuthor(opt);
  };

  const safeSetEditor = (opt) => {
    if (!canAssignPeople || !canEditMeta) return;    // ignore attempts
    setEditor(opt);
  };

  const customStyles = useMemo(() => ({
    control: (provided) => ({
      ...provided,
      boxShadow: "none",
      borderRadius: 8,
      border: "1px solid #ccc",
      backgroundColor: "#ffffff",
      color: "#333",
      transition: "border-color 0.2s ease, box-shadow 0.2s ease"
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 2, // dropdown appears above other content
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#FFFFFF", // Background color of selected options
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#000000", // Text color of selected options
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#272635", // Color of the dropdown arrow
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: "#272635", // Color of the separator between the arrow and the input
    }),
  }), []);

  // Badge styling
  const badgeStyle = (stageKey) => {
    return {
      padding: "4px 10px",
      borderRadius: 15,
      fontSize: 12,
      fontWeight: 600,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      lineHeight: 1.2,
      color: "#111",
      background: "#f5f5f5",
      border: `2px solid ${stageColor(stageKey)}`,
      flex: "0 0 auto",
      width: "auto",
      whiteSpace: "nowrap",
    };
  };

  const listEstWordCount = useMemo(() => ([
    { value: "500-999", label: "500–999" },
    { value: "1000-1499", label: "1,000–1,499" },
    { value: "1500-1999", label: "1,500–1,999" },
    { value: "2000-2499", label: "2,000–2,499" },
    { value: "2500+", label: "2,500+" },
  ]), []);

  const getDaysRemaining = (iso) => {
    if (!iso) return null;
    const due = new Date(iso);
    const now = new Date();

    // Clear time for accurate day difference
    due.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);

    const diffTime = due - now;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    let text = "";
    let color = "";

    if (diffDays > 2) {
      text = `${diffDays} days remaining`;
      color = "green";
    } else if (diffDays > 0) {
      text = `${diffDays} day${diffDays === 1 ? "" : "s"} remaining`;
      color = "orange";
    } else if (diffDays === 0) {
      text = "Due today";
      color = "orange";
    } else {
      text = `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? "" : "s"}`;
      color = "red";
    }

    return { text, color };
  };

  return (
    <>
      <div className="metadata-grid meta-panel">
        <div className="writeFormGroup field">
          <label htmlFor="author-select" className="meta-label">
            Author
          </label>
          {canAssignPeople ? (
            <div className="select author">
              <Select
                options={authors || []}
                value={mainAuthor}
                onChange={safeSetMainAuthor}
                isDisabled={!canEditMeta}
                styles={customStyles}
                placeholder="Select Author *"
                inputId="author-select"
                isClearable={false}
              />
            </div>
            ) : (
              <span
                className="locked-pill"
                role="textbox"
                aria-readonly="true"
                aria-label="Author (auto-assigned)"
                title="Only Lead Editors and Admins can change this"
              >
                {mainAuthor?.label
                  ? mainAuthor.label
                  : (!authors || authors.length === 0)
                    ? "Loading..."
                    : "Auto-assigned"}
              </span>
            )}
          <div className="writeFieldSpacer"></div>
        </div>

        <div className="writeFormGroup field">
          <label htmlFor="editor-select" className="meta-label">
            Editor
          </label>
          {canAssignPeople ? (
            <div className="select author">
              <Select
                options={editors || []}
                value={editor}
                onChange={safeSetEditor}
                isDisabled={!canEditMeta}
                styles={customStyles}
                placeholder="Select Editor *"
                inputId="editor-select"
                isClearable={false}
              />
            </div>
            ) : (
              <span
                className="locked-pill"
                role="textbox"
                aria-readonly="true"
                aria-label="Editor (awaiting assignment)"
                title="Only Lead Editors and Admins can change this"
              >
                {editor?.label || "Awaiting assignment"}
              </span>
            )}
          <div className="writeFieldSpacer"></div>
        </div>

        {/*
        <div className="optionClass">
          <div className="categories field">
            <label className="meta-label">
              Co-Authors
            </label>
            <div className="select">
              <Select
                options={authors.filter(
                  (author) => author.value !== mainAuthor?.value
                )}
                isMulti
                value={selectedAuthors}
                onChange={(opts) =>
                  setSelectedAuthors(
                    opts.map((opt) => ({
                      label: opt.label,
                      value: decodeHtml(opt.value),
                    }))
                  )
                }
                isDisabled={!canEditMeta}
                styles={customStyles}
              />
            </div>
            <div className="writeFieldSpacer"></div>
          </div>
        </div>
        */}

        <div className="writeFormGroup field">
          <label className="meta-label">
            Title
          </label>
          <input
            type="text"
            placeholder="Title *"
            className="writeInput"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={!canEditMeta}
          />
          <div className="writeFieldSpacer"></div>
        </div>


        <div className="writeFormGroup field">
          <label className="meta-label">
            SEO Title
            <button
              type="button"
              className="info-icon"
              onClick={() => toggleTooltip("seoTitle")}
              aria-label="SEO Title help"
            >
              i
            </button>
            {visibleTooltip === "seoTitle" && (
              <span className="tooltip">The title shown in search results. Start with the focus keyphrase.</span>
            )}
          </label>
          <input
            type="text"
            placeholder="SEO Title *"
            className="writeInput"
            value={seoTitle}
            onChange={(e) => setSeoTitle(e.target.value)}
            disabled={!canEditMeta}
          />
        </div>

        <div className="optionClass">
          <div className="categories field">
            <label className="meta-label">
              Categories
              <button
                type="button"
                className="info-icon"
                onClick={() => toggleTooltip("categories")}
                aria-label="Categories help"
              >
                i
              </button>
              {visibleTooltip === "categories" && (
                <span className="tooltip">The main topics this article belongs to.</span>
              )}
            </label>
            <div className="select">
              <Select
                options={categories}
                isMulti
                value={selectedCategories}
                onChange={(opts) => setSelectedCategories(
                  (opts || []).filter(Boolean).map(opt => ({
                    label: opt.label || "",
                    value: decodeHtml(String(opt.value || "")),
                  })).filter(x => x.value)
                )}
                styles={customStyles}
                placeholder="Select Categories *"
                isDisabled={!canEditMeta}
              />
            </div>
            <div className="writeFieldSpacer"></div>
          </div>

          <div className="tags field">
            <label className="meta-label">
              Tags
              <button
                type="button"
                className="info-icon"
                onClick={() => toggleTooltip("tags")}
                aria-label="Tags help"
              >
                i
              </button>
              {visibleTooltip === "tags" && (
                <span className="tooltip">Specific keywords that help group related articles.</span>
              )}
            </label>
            <div className="select">
              <CreatableSelect
                isMulti
                options={tags}
                value={selectedTags}
                onChange={handleTagChange}
                styles={customStyles}
                placeholder="Select or create tags"
                isDisabled={!canEditMeta}
              />
            </div>
            <div className="writeFieldSpacer"></div>
          </div>
        </div>

        <div className="writeFormGroup field">
          <label className="meta-label">
            Slug
            <button
              type="button"
              className="info-icon"
              onClick={() => toggleTooltip("slug")}
              aria-label="Slug help"
            >
              i
            </button>
            {visibleTooltip === "slug" && (
              <span className="tooltip">The readable part of a web address that describes the page. For SEO, include the focus keyphrase in the slug (exact match is ideal when natural).</span>
            )}
          </label>
          <input
            type="text"
            placeholder="Slug *"
            className="writeInput"
            value={postSlug}
            onChange={(e) => setPostSlug(e.target.value)}
            disabled={!canEditMeta}
          />
        </div>

        <div className="writeFormGroup field">
          <label className="meta-label">
            Focus Keyphrase
            <button
              type="button"
              className="info-icon"
              onClick={() => toggleTooltip("focusKeyphrase")}
              aria-label="Focus Keyphrase help"
            >
              i
            </button>
            {visibleTooltip === "focusKeyphrase" && (
              <span className="tooltip">The main search phrase this article targets. Use 3–5 words.</span>
            )}
          </label>
          <input
            type="text"
            placeholder="Focus Keyphrase *"
            className="writeInput"
            value={focusKeyphrase}
            onChange={(e) => setFocusKeyphrase(e.target.value)}
            disabled={!canEditMeta}
          />
        </div>

        <div className="writeFormGroup field">
          <label className="meta-label">
            Est. Word Count
            <button
              type="button"
              className="info-icon"
              onClick={() => toggleTooltip("estWordCount")}
              aria-label="Est. Word Count help"
            >
              i
            </button>
            {visibleTooltip === "estWordCount" && (
              <span className="tooltip">Estimated final word count. Helps editors plan scope and deadlines.</span>
            )}
          </label>
          <div className="select estWordCount">
            <Select
              options={listEstWordCount}
              value={estWordCount}
              onChange={(opt) => setEstWordCount(opt)}
              styles={customStyles}
              placeholder="Select Estimated Word Count *"
              isDisabled={!canEditMeta}
            />
          </div>
          <div className="writeFieldSpacer"></div>
        </div>

        <div className="writeFormGroup field">
          <label className="meta-label">
            Final Draft Due
          </label>
          <span className="final-draft-due">
            {finalDraftDue
              ? new Date(finalDraftDue).toLocaleString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              : "—"}
          {finalDraftDue && (() => {
            const remaining = getDaysRemaining(finalDraftDue);
            if (!remaining) return null;
            const { text, color } = remaining;
            const background =
                    color === "green"
                      ? "rgba(0, 128, 0, 0.1)"
                      : color === "orange"
                      ? "rgba(255, 165, 0, 0.15)"
                      : "rgba(255, 0, 0, 0.1)";

            return (
              <span
                style={{
                  color,
                  background,
                  border: `1px solid ${color}`,
                  borderRadius: "12px",
                  padding: "2px 8px",
                  marginLeft: "5px",
                  fontSize: "13px",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {text}
              </span>
            );
          })()}
          </span>

          {/*
          <div className="tooltip-container">
            <i className="info-icon" onClick={() => toggleTooltip("finalDraftDue")}>i</i>
            {visibleTooltip === "finalDraftDue" && <div className="tooltip">Automatically calculated from Estimated Word Count and the date the draft was created.</div>}
          </div>
          */}

        </div>

        <div className="writeFormGroup field">
          <label className="meta-label">
            Meta Description
            <button
              type="button"
              className="info-icon"
              onClick={() => toggleTooltip("metaDesc")}
              aria-label="Meta Description help"
            >
              i
            </button>
            {visibleTooltip === "metaDesc" && (
              <span className="tooltip">A short summary shown in search results (not the article intro). Include the focus keyphrase and keep it between 120–156 characters.</span>
            )}
          </label>
          <textarea
            type="text"
            placeholder="Meta Description *"
            className="writeInput"
            value={metaDescription}
            onChange={(e) => {
              setMetaDescription(e.target.value);
              e.target.style.height = "36px"; // reset first
              e.target.style.height = e.target.scrollHeight + "px"; // auto-expand
            }}
            rows={1}
            disabled={!canEditMeta}
          />
        </div>

        <div className="featured-images field">
          <label className="meta-label">
            Featured Image
            <button
              type="button"
              className="info-icon"
              onClick={() => toggleTooltip("featuredImage")}
              aria-label="Featured Image help"
            >
              i
            </button>
            {visibleTooltip === "featuredImage" && (
              <span className="tooltip">The main image shown on the homepage and article previews.</span>
            )}
          </label>
          <div
            className="featured-images-container"
            onClick={() => {
              if (!canEditMeta) return;
              setShowFeatureImageModal(true);
            }}
            tabIndex={canEditMeta ? 0 : -1}
            role="button"
            aria-disabled={!canEditMeta}
            onKeyDown={(e) => {
              if (!canEditMeta) return;
              if (e.key === "Enter" || e.key === " ") setShowFeatureImageModal(true);
            }}
          >
            {featuredImageData?.src ? (
              <div className="image-wrapper">
                <img
                  src={featuredImageData.src}
                  alt={featuredImageData.alt || "Featured preview"}
                />
                {featuredImageData.attribution && (
                  <p className="image-attribution">Credit: {featuredImageData.attribution}</p>
                )}
                {featuredImageData.caption && (
                  <p className="image-caption">Caption: {featuredImageData.caption}</p>
                )}
                <button
                  className="remove-image-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!canEditMeta) return;
                    setFeaturedImageData(null);
                  }}
                  disabled={!canEditMeta}
                  type="button"
                >
                  ✖️
                </button>
              </div>
            ) : (
              <div className="placeholder">Click to upload featured image</div>
            )}
          </div>
        </div>

        <div className="writeFormGroup field">
          <label className="meta-label">
            Stage
          </label>
          {canChangeStage ? (
            <div className="select author">
              <Select
                isSearchable={false}
                styles={customStyles}
                options={STAGE_ORDER.map(k => ({ value: k, label: toHuman(k) }))}
                value={{ value: stage, label: toHuman(stage) }}
                isDisabled={isSavingStage}
                onChange={async (opt) => {
                  if (!opt?.value || opt.value === stage) return;
                  const newKey = opt.value;
                  const prev = stage;
                  setStage(newKey);
                  try {
                    setIsSavingStage(true);
                    await axios.put(`/posts/${currentArticleId}`, { stage: newKey }, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    toast.success("Stage updated");
                  } catch (err) {
                    setStage(prev); // revert
                    toast.error("Failed to update stage");
                  } finally {
                    setIsSavingStage(false);
                  }
                }}
                placeholder="Select Stage"
              />
            </div>
          ) : (
            <span style={badgeStyle(stage)}>
              <span
                aria-hidden
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: stageColor(stage),
                  display: "inline-block"
                }}
              />
              {toHuman(stage)}
            </span>
          )}
        </div>
      </div>
    </>
  );
}
